/**
 * Adapters between RelativeForm's FH v2 body shape (camelCase, sent to
 * api-family-history-v2-service today) and the transcript's captured-
 * item payload shape (snake_case, matches what the Python agent emits
 * and what Go's SaveSection fan-out reads on Save & next).
 *
 * Same pattern as complaintBodyAdapters / diagnosisBodyAdapters /
 * medicineBodyAdapters / hospitalisationBodyAdapters /
 * labInvestigationBodyAdapters / allergyBodyAdapters.
 *
 *   FH v2 add body            → captured "add" payload
 *   {relationship, customRelation?, conditions:[{name, source}],
 *    description}
 *      →
 *   {relationship, custom_relation, conditions:[{name, source}],
 *    description, value}
 *
 * `value` is the display string the review table + captured-item cards
 * render as the "title" of the row. Mirrors how the manual card is
 * shaped by the section's own display code.
 */

const displayValue = (body) => {
  const rel = (body.relationship || "other").toString();
  if (rel === "other") return (body.customRelation || "Other").trim() || "Other";
  return rel.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

/**
 * FH v2 add body → captured "add" payload.
 */
export function bodyToCapturedAdd(body) {
  const conds = Array.isArray(body.conditions) ? body.conditions : [];
  const label = displayValue(body);
  const condName = conds.length > 0 ? (conds[0].name || "").trim() : "";
  return {
    relationship:    body.relationship || "other",
    custom_relation: body.customRelation || "",
    conditions:      conds.map((c) => {
      const icd = (c && (c.icdCode || c.icd_code || c.code)) || "";
      const out = {
        name:   (c && c.name) || "",
        // Preserve source verbatim when present; default to "icd" when
        // we have a code, else "free-text". Standalone's Common
        // Conditions rail + IcdAutocomplete both stamp source="icd" +
        // an icdCode, and the FH v2 read path renders the code chip
        // from these fields.
        source: (c && c.source) || (icd ? "icd" : "free-text"),
      };
      if (icd) out.icdCode = icd;
      return out;
    }).filter((c) => c.name),
    description:     body.description || "",
    // Display convention for the review table + captured-cards. Matches
    // the agent's own display_value ("<Rel> — <first-condition>").
    value:           condName ? `${label} — ${condName}` : label,
    name:            label,
  };
}

/**
 * Stub — Wave B first-cut doesn't route Family history updates via AI.
 * Symmetric with the other section adapters and used by ReviewSave's
 * click-to-edit re-open path.
 */
export function bodyToCapturedUpdate(body /* , context */) {
  return bodyToCapturedAdd(body);
}
